// CommitPanelToggle — client-side toggle for the diff commit panel (ts-2).
//
// Replaces the server-side `toggle_commit_panel` / `commit_panel_open`
// boolean assign in diff_component.ex.
//
// Markup convention:
//   Attach phx-hook="CommitPanelToggle" to the <diff-view> root.
//   Toggle trigger:   <el data-toggle-commit-panel>…</el>
//   Collapsible panel: <el data-commit-panel class="…collapsed-class…">…</el>
//
// The panel's CSS class `collapsed` is already defined in the inline
// <style> block in diff_component.ex; we toggle it here instead of
// relying on Phoenix to re-render the `collapsed` conditional.
// State uses the shared `_panelOpen` convention (./panel_state): the
// hook element is the wrapper containing the panel, so `updated()`
// re-asserting state is enough to survive patches.

import type { LiveViewHook } from "./types";
import type { PanelState } from "./panel_state";

const applyState = (el: HTMLElement, open: boolean): void => {
  const panel = el.querySelector<HTMLElement>("[data-commit-panel]");
  panel?.classList.toggle("collapsed", !open);
};

export interface CommitPanelToggleState extends PanelState {
  _onClick: (e: Event) => void;
}

export const CommitPanelToggle = {
  mounted(this: LiveViewHook<never> & CommitPanelToggleState) {
    this._panelOpen = true;

    const onClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-toggle-commit-panel]",
      );
      if (!btn) return;
      this._panelOpen = !this._panelOpen;
      applyState(this.el, this._panelOpen);
    };
    this.el.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  updated(this: LiveViewHook<never> & CommitPanelToggleState) {
    applyState(this.el, this._panelOpen);
  },

  destroyed(this: LiveViewHook<never> & CommitPanelToggleState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
  },
};
